/**
 * Locations summary table for the GHL Qualifier landing page.
 *
 * One row per location, summarizing qualifier + converted activity within
 * the current time range. Click → drill into per-location detail.
 *
 * Filtering / sorting:
 *   - Each numeric column has an inline filter dropdown (any / has / none)
 *   - The activity column filter uses time-window buckets (24h / 7d / 30d+)
 *   - Every column header is clickable to sort ascending/descending
 *
 * The component is purely presentational — sort+filter STATE lives in the
 * parent page, applied via `useMemo`. Here we only render UI controls and
 * bubble change events back up.
 */

import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react'
import { formatRelativeTime } from '@/lib/ghl-qualifier'
import {
  useQualifierGoogleAdsFlowMap,
  type QualifierLocationOverviewRow,
} from '@/hooks/useGHLQualifierDashboard'
import { GoogleAdsFlowBadge } from './GoogleAdsFlowBadge'

// ──────────────────────────────────────────────────────────────────────────
// Types — exported so the parent page can hold matching state
// ──────────────────────────────────────────────────────────────────────────

export type LocationsTableSortColumn =
  | 'location'
  | 'qualified'
  | 'converted'
  | 'activity'

export interface LocationsTableSort {
  readonly by: LocationsTableSortColumn
  readonly dir: 'asc' | 'desc'
}

/**
 * Numeric column filter values — collapses each numeric column to a
 * three-state filter so operators don't have to think in number ranges.
 *   - 'any':  no filter
 *   - 'has':  count > 0 in this column
 *   - 'none': count = 0 in this column
 */
export type NumericColumnFilterValue = 'any' | 'has' | 'none'

/**
 * Activity column filter values — time-window buckets relative to now.
 *   - 'last_24h':  active within the past 24 hours
 *   - 'last_7d':   active within the past 7 days
 *   - 'stale_30d': no activity in the past 30 days (includes never-active)
 */
export type ActivityFilterValue = 'any' | 'last_24h' | 'last_7d' | 'stale_30d'

export interface LocationsTableFilters {
  readonly qualified: NumericColumnFilterValue
  readonly converted: NumericColumnFilterValue
  readonly activity: ActivityFilterValue
}

interface Props {
  readonly data: readonly QualifierLocationOverviewRow[]
  readonly isLoading?: boolean
  readonly onRowClick: (locationId: string) => void
  readonly filters: LocationsTableFilters
  readonly onFiltersChange: (next: LocationsTableFilters) => void
  readonly sort: LocationsTableSort
  readonly onSort: (column: LocationsTableSortColumn) => void
}

// ──────────────────────────────────────────────────────────────────────────
// Sub-components
// ──────────────────────────────────────────────────────────────────────────

/**
 * A clickable column header that displays the current sort state via icon.
 * Uses a transparent button so the header stays keyboard-accessible.
 */
function SortableHeader({
  label,
  column,
  sort,
  onSort,
  align,
}: {
  label: string
  column: LocationsTableSortColumn
  sort: LocationsTableSort
  onSort: (column: LocationsTableSortColumn) => void
  align?: 'left' | 'right'
}) {
  const isActive = sort.by === column
  const Icon = !isActive ? ArrowUpDown : sort.dir === 'asc' ? ArrowUp : ArrowDown
  return (
    <button
      type="button"
      onClick={() => onSort(column)}
      className={`flex items-center gap-1 font-semibold w-full ${
        align === 'right' ? 'justify-end' : 'justify-start'
      } ${isActive ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
      aria-label={`Sort by ${label} ${
        !isActive ? '' : sort.dir === 'asc' ? 'ascending' : 'descending'
      }`}
    >
      <span>{label}</span>
      <Icon className={`h-3.5 w-3.5 ${isActive ? 'opacity-100' : 'opacity-40'}`} />
    </button>
  )
}

/**
 * Compact select used inside the filter row. Native-ish look with shadcn
 * styling so it doesn't overwhelm the column it belongs to. Defaults
 * showing a placeholder when the value is 'any' so the row looks clean
 * when nothing is filtered.
 */
function ColumnFilterSelect<T extends string>({
  value,
  onChange,
  options,
  ariaLabel,
}: {
  value: T
  onChange: (next: T) => void
  options: readonly { value: T; label: string }[]
  ariaLabel: string
}) {
  return (
    <Select value={value} onValueChange={(v) => onChange(v as T)}>
      <SelectTrigger className="h-7 text-xs px-2" aria-label={ariaLabel}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value} className="text-xs">
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

const NUMERIC_OPTIONS: readonly { value: NumericColumnFilterValue; label: string }[] = [
  { value: 'any', label: 'Any' },
  { value: 'has', label: '> 0' },
  { value: 'none', label: '= 0' },
]

const ACTIVITY_OPTIONS: readonly { value: ActivityFilterValue; label: string }[] = [
  { value: 'any', label: 'Any time' },
  { value: 'last_24h', label: 'Last 24h' },
  { value: 'last_7d', label: 'Last 7 days' },
  { value: 'stale_30d', label: 'Stale (30d+)' },
]

// ──────────────────────────────────────────────────────────────────────────
// Main component
// ──────────────────────────────────────────────────────────────────────────

export function QualifierLocationsTable({
  data,
  isLoading,
  onRowClick,
  filters,
  onFiltersChange,
  sort,
  onSort,
}: Props) {
  const gaFlow = useQualifierGoogleAdsFlowMap()

  function setFilter<K extends keyof LocationsTableFilters>(
    key: K,
    value: LocationsTableFilters[K]
  ) {
    onFiltersChange({ ...filters, [key]: value })
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          {/* Filter row — sits above the title row so operators can spot
              filtered columns at a glance. Each cell aligns to its column. */}
          <TableRow className="hover:bg-transparent border-b-0">
            <TableHead className="w-[280px]">
              <span className="text-xs uppercase tracking-wide text-muted-foreground">
                Filter
              </span>
            </TableHead>
            <TableHead>
              <div className="flex justify-start">
                <ColumnFilterSelect
                  value={filters.qualified}
                  onChange={(v) => setFilter('qualified', v)}
                  options={NUMERIC_OPTIONS}
                  ariaLabel="Filter qualified"
                />
              </div>
            </TableHead>
            <TableHead>
              <div className="flex justify-start">
                <ColumnFilterSelect
                  value={filters.converted}
                  onChange={(v) => setFilter('converted', v)}
                  options={NUMERIC_OPTIONS}
                  ariaLabel="Filter converted"
                />
              </div>
            </TableHead>
            <TableHead>
              <div className="flex justify-end">
                <ColumnFilterSelect
                  value={filters.activity}
                  onChange={(v) => setFilter('activity', v)}
                  options={ACTIVITY_OPTIONS}
                  ariaLabel="Filter activity"
                />
              </div>
            </TableHead>
          </TableRow>

          {/* Column titles — clickable for sorting */}
          <TableRow>
            <TableHead>
              <SortableHeader
                label="Location"
                column="location"
                sort={sort}
                onSort={onSort}
                align="left"
              />
            </TableHead>
            <TableHead>
              <SortableHeader
                label="Qualification"
                column="qualified"
                sort={sort}
                onSort={onSort}
                align="left"
              />
            </TableHead>
            <TableHead>
              <SortableHeader
                label="Conversion"
                column="converted"
                sort={sort}
                onSort={onSort}
                align="left"
              />
            </TableHead>
            <TableHead className="text-right">
              <SortableHeader
                label="Last Activity"
                column="activity"
                sort={sort}
                onSort={onSort}
                align="right"
              />
            </TableHead>
          </TableRow>
        </TableHeader>

        <TableBody>
          {isLoading
            ? Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={4}>
                    <Skeleton className="h-5 w-full" />
                  </TableCell>
                </TableRow>
              ))
            : data.map((row) => {
                // Prefer the RPC-provided precise count (added in migration
                // 20260515100000). The fallback derivation handles the
                // transitional window where the RPC has been deployed but
                // the field is missing from a cached query — and any odd
                // case where the new column comes back null. The derived
                // formula `total − unqualified − pending` is slightly
                // imprecise for locations with cancelled/failed rows (it
                // includes them in the "qualified" bucket) but is the
                // best we can do without the RPC's COUNT FILTER.
                const qualifiedCount =
                  row.qualified_qualified !== undefined &&
                  row.qualified_qualified !== null
                    ? row.qualified_qualified
                    : Math.max(
                        0,
                        row.qualified_total -
                          row.qualified_unqualified -
                          row.qualified_pending
                      )
                return (
                  <TableRow
                    key={row.location_id}
                    className="cursor-pointer hover:bg-muted/40"
                    onClick={() => onRowClick(row.location_id)}
                  >
                    <TableCell>
                      <div className="flex flex-col">
                        <div className="flex items-center gap-1.5">
                          <span className="font-medium" title={row.location_name}>
                            {row.location_name}
                          </span>
                          {gaFlow.data?.has(row.location_id) ? <GoogleAdsFlowBadge /> : null}
                        </div>
                        <span className="text-xs text-muted-foreground font-mono">
                          {row.location_id}
                        </span>
                      </div>
                    </TableCell>
                    {/*
                      Qualification + Conversion cells use spelled-out breakdown
                      text — numbers are tabular-nums for column-alignment
                      consistency, labels are muted so the numbers pop. The
                      cell uses flex-wrap so wide values wrap onto a second
                      line gracefully on narrower viewports.
                    */}
                    <TableCell>
                      <div className="flex flex-wrap items-baseline gap-x-1 text-sm">
                        <span className="tabular-nums font-medium">
                          {row.qualified_unqualified.toLocaleString()}
                        </span>
                        <span className="text-muted-foreground">unqualified</span>
                        <span className="text-muted-foreground/40 mx-1">·</span>
                        <span className="tabular-nums font-medium">
                          {qualifiedCount.toLocaleString()}
                        </span>
                        <span className="text-muted-foreground">qualified</span>
                        <span className="text-muted-foreground/40 mx-1">·</span>
                        <span className="tabular-nums font-medium">
                          {row.qualified_pending.toLocaleString()}
                        </span>
                        <span className="text-muted-foreground">pending</span>
                        <span className="text-muted-foreground/40 mx-1">·</span>
                        <span className="tabular-nums font-medium">
                          {row.qualified_total.toLocaleString()}
                        </span>
                        <span className="text-muted-foreground">total</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap items-baseline gap-x-1 text-sm">
                        <span className="tabular-nums font-medium">
                          {row.converted_completed.toLocaleString()}
                        </span>
                        <span className="text-muted-foreground">confirmed</span>
                        <span className="text-muted-foreground/40 mx-1">·</span>
                        <span className="tabular-nums font-medium">
                          {row.converted_exhausted.toLocaleString()}
                        </span>
                        <span className="text-muted-foreground">exhausted</span>
                        <span className="text-muted-foreground/40 mx-1">·</span>
                        <span className="tabular-nums font-medium">
                          {row.converted_in_progress.toLocaleString()}
                        </span>
                        <span className="text-muted-foreground">pending</span>
                        <span className="text-muted-foreground/40 mx-1">·</span>
                        <span className="tabular-nums font-medium">
                          {row.converted_total.toLocaleString()}
                        </span>
                        <span className="text-muted-foreground">total</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground">
                      {row.last_activity_at ? formatRelativeTime(row.last_activity_at) : '—'}
                    </TableCell>
                  </TableRow>
                )
              })}
        </TableBody>
      </Table>
    </div>
  )
}
